import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { NAV_ITEMS, NAV_DEMO_BTN, VITRINE_BASE } from '../constants';
import { useLanguage } from '../LanguageContext';

// ─── SVG drapeaux inline ─────────────────────────────────────────────────────
const FlagFR = () => (
  <svg width="18" height="13" viewBox="0 0 18 13" aria-hidden="true" style={{ borderRadius: 2 }}>
    <rect width="6"  height="13" fill="#0055A4" />
    <rect x="6"  width="6" height="13" fill="#EDEDED" />
    <rect x="12" width="6" height="13" fill="#EF4135" />
  </svg>
);

const FlagEN = () => (
  <svg width="18" height="13" viewBox="0 0 18 13" aria-hidden="true" style={{ borderRadius: 2 }}>
    <rect width="18" height="13" fill="#012169" />
    <path d="M0,0 L18,13 M18,0 L0,13" stroke="#fff" strokeWidth="2.5" />
    <path d="M0,0 L18,13 M18,0 L0,13" stroke="#C8102E" strokeWidth="1.5" />
    <path d="M9,0 V13 M0,6.5 H18" stroke="#fff" strokeWidth="4" />
    <path d="M9,0 V13 M0,6.5 H18" stroke="#C8102E" strokeWidth="2.5" />
  </svg>
);

export default function Navigation() {
  const [scrolled, setScrolled]     = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, setLang }           = useLanguage();
  const items                       = NAV_ITEMS[lang];
  const demoLabel                   = NAV_DEMO_BTN[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Sur une sous-page (ex: /demo), naviguer vers la page principale avec l'ancre
      window.location.href = `${VITRINE_BASE}/${href}`;
    }
  };

  return (
    <>
      <header className={`pb-nav${scrolled ? ' scrolled' : ''}`} data-testid="pb-nav">
        <div className="pb-nav-inner">
          {/* Logo */}
          <a href="#accueil" className="pb-nav-logo" onClick={e => scrollTo(e, '#accueil')} data-testid="pb-nav-logo">
            <div className="pb-nav-logo-mark">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                <rect width="28" height="28" rx="6" fill="#0A192F"/>
                <rect x="7" y="7" width="3" height="14" fill="white" rx="1"/>
                <path d="M10 7h6a4 4 0 0 1 0 8h-6z" fill="white"/>
                <rect x="7" y="19" width="10" height="2" rx="1" fill="#0055A4"/>
              </svg>
            </div>
            <span className="pb-nav-logo-name">PitBoard<span>.</span></span>
          </a>

          {/* Desktop links */}
          <nav aria-label={lang === 'fr' ? 'Navigation principale' : 'Main navigation'}>
            <ul className="pb-nav-links">
              {items.map(item => (
                <li key={item.label}>
                  <a href={item.href} onClick={e => scrollTo(e, item.href)}>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* CTA + sélecteur langue + mobile toggle */}
          <div className="pb-nav-right">
            {/* Sélecteur de langue */}
            <div className="pb-lang-toggle" role="group" aria-label={lang === 'fr' ? 'Langue' : 'Language'} data-testid="pb-lang-toggle">
              <button
                className={`pb-lang-btn${lang === 'fr' ? ' active' : ''}`}
                onClick={() => setLang('fr')}
                aria-pressed={lang === 'fr'}
                data-testid="pb-lang-fr"
              >
                <FlagFR /> FR
              </button>
              <button
                className={`pb-lang-btn${lang === 'en' ? ' active' : ''}`}
                onClick={() => setLang('en')}
                aria-pressed={lang === 'en'}
                data-testid="pb-lang-en"
              >
                <FlagEN /> EN
              </button>
            </div>

            <button
              className="pb-btn-primary"
              style={{ padding: '9px 20px', fontSize: '14px' }}
              onClick={() => window.dispatchEvent(new CustomEvent('openDemoModal'))}
              data-testid="pb-nav-demo-btn"
            >
              {demoLabel}
            </button>

            <button
              className="pb-nav-mobile-btn"
              aria-label="Menu"
              onClick={() => setMobileOpen(o => !o)}
              data-testid="pb-nav-mobile-toggle"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          style={{
            position: 'fixed', top: 64, left: 0, right: 0, zIndex: 999,
            background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(10,25,47,0.08)',
            padding: '16px 20px 20px',
          }}
          data-testid="pb-nav-mobile-menu"
        >
          {items.map(item => (
            <a
              key={item.label}
              href={item.href}
              onClick={e => scrollTo(e, item.href)}
              style={{
                display: 'block', padding: '12px 4px',
                fontSize: '16px', fontWeight: 500,
                color: '#475569', textDecoration: 'none',
                borderBottom: '1px solid rgba(10,25,47,0.05)',
              }}
            >
              {item.label}
            </a>
          ))}
          <button
            className="pb-btn-primary"
            style={{ display: 'block', textAlign: 'center', marginTop: 16 }}
            onClick={() => { setMobileOpen(false); window.dispatchEvent(new CustomEvent('openDemoModal')); }}
            data-testid="pb-nav-mobile-demo-btn"
          >
            {demoLabel}
          </button>
        </div>
      )}
    </>
  );
}
